;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { PROVIDER_NAMES } from "@/components/settings/data-providers/constants";
import { PROVIDER_FORM_REGISTRY } from "@/components/settings/data-providers/provider-forms";
import { mergeRentvineVendorOptions, toRentvineVendorOptions } from "@/components/settings/data-providers/provider-forms/rentvine-vendor-options";
import { Button } from "@/components/ui/button";
import { Credenza, CredenzaClose, CredenzaContent, CredenzaDescription, CredenzaFooter, CredenzaHeader, CredenzaTitle } from "@/components/ui/credenza";
import { Form } from "@/components/ui/form";
import { usePropertyGroups } from "@/hooks/use-property-groups";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { useDataProviders } from "@/hooks/useDataProviders";
import { emitter, type SettingsDataProviderEmitterState } from "@/lib/emitter";
import { type EditDataProviderFormValues, type EditDataProviderSchema, editDataProviderSchema } from "@/lib/schema/data-provider";
import type { DataProvider } from "@/prisma/generated/client";
import { ProviderType } from "@/types/enums";

/** Controller for the edit data provider modal */
export function controlEditDataProvider(properties: SettingsDataProviderEmitterState) {
  emitter.emit("settings.workspace.data_provider.edit", properties);
}
export function EditDataProvider() {
  const {
    workspaceId
  } = useCurrentWorkspace();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [isOpen, setOpenState] = useState(false);
  const {
    editWorkspaceDataProvider,
    editingWorkspaceDataProvider: isEditing,
    dataProviders,
    pingWorkspaceDataProvider
  } = useDataProviders();
  const handleEditEvent = useCallback(({
    data,
    state
  }: SettingsDataProviderEmitterState) => {
    setOpenState(state === "on");
    if (!data.providerId) {
      setTimeout(() => {
        setProviderId(data.providerId);
      }, 300);
    } else {
      setProviderId(data.providerId);
    }
  }, []);
  const handleCloseEvent = useCallback(() => {
    setProviderId(null);
    setOpenState(false);
  }, []);
  useEffect(() => {
    emitter.on("settings.workspace.data_provider.edit", handleEditEvent);
    return () => {
      emitter.off("settings.workspace.data_provider.edit", handleEditEvent);
      handleCloseEvent();
    };
  }, [handleEditEvent, handleCloseEvent]);
  const selectedProvider = useMemo(() => {
    return dataProviders.find(provider => provider.id === providerId);
  }, [providerId, dataProviders]);
  const {
    type = ProviderType.APP_FOOLIO,
    id = ""
  } = (selectedProvider ?? {}) as DataProvider;
  const databaseName = selectedProvider?.credentials[0]?.databaseName as string | undefined;

  // Saved property group ids in credentials JSON (if any)
  const savedPropertyGroupIds = (((selectedProvider?.credentials?.[0] as any)?.credentials?.propertyGroupIds ?? []) as unknown[]).map(groupId => String(groupId).trim()).filter(groupId_0 => groupId_0.length > 0);
  const savedAsanaProjectIds = ((selectedProvider?.credentials?.[0] as any)?.credentials?.projectIds ?? []) as string[];
  const savedAsanaProjectNames = ((selectedProvider?.credentials?.[0] as any)?.credentials?.projectNames ?? []) as string[];
  const savedAsanaWorkspaceGid = ((selectedProvider?.credentials?.[0] as any)?.credentials?.workspaceGid ?? "") as string;
  const savedAsanaWorkspaceName = ((selectedProvider?.credentials?.[0] as any)?.credentials?.workspaceName ?? "") as string;
  const savedRentvineVendorContactIds = (((selectedProvider?.credentials?.[0] as any)?.credentials?.generalWorkOrderVendorContactIds ?? []) as unknown[]).filter((contactId): contactId is string | number => typeof contactId === "string" || typeof contactId === "number").map(contactId_0 => String(contactId_0).trim()).filter(contactId_1 => contactId_1.length > 0);
  const [asanaWorkspaceOptions, setAsanaWorkspaceOptions] = useState<{
    label: string;
    value: string;
  }[]>([]);
  const [asanaProjectOptions, setAsanaProjectOptions] = useState<{
    label: string;
    value: string;
  }[]>([]);
  const [hasAsanaProjectOptionsRefreshSettled, setHasAsanaProjectOptionsRefreshSettled] = useState(false);
  const [rentvineVendorOptions, setRentvineVendorOptions] = useState<{
    label: string;
    value: string;
  }[]>([]);
  const [rentvineVendorOptionsLoading, setRentvineVendorOptionsLoading] = useState(false);
  const [rentvineVendorOptionsError, setRentvineVendorOptionsError] = useState<string | null>(null);

  // Fetch property groups for this workspace
  const {
    data: propertyGroups = []
  } = usePropertyGroups();
  const propertyGroupOptions = [...(propertyGroups as any[])
  // sort by numeric groupId ascending
  ].sort((a, b) => {
    const aId = Number(a.groupId ?? a.id ?? 0);
    const bId = Number(b.groupId ?? b.id ?? 0);
    return aId - bId;
  }).map(g => ({
    label: String(g.groupName ?? g.name ?? "Unnamed Group"),
    value: String(g.groupId ?? g.id)
  }));

  // Provider data for forms (e.g., AppFolio needs property groups on step 1)
  const providerData = useMemo(() => {
    if (type === ProviderType.APP_FOOLIO) {
      const savedPropertyGroupOptions = savedPropertyGroupIds.map(groupId_1 => ({
        label: groupId_1,
        value: groupId_1
      }));
      const mergedPropertyGroupOptions = Array.from(new Map([...savedPropertyGroupOptions, ...propertyGroupOptions].map(option => [option.value, option])).values()).sort((optionA, optionB) => {
        const aNum = Number(optionA.value);
        const bNum = Number(optionB.value);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
        return optionA.value.localeCompare(optionB.value);
      });
      return {
        propertyGroupOptions: mergedPropertyGroupOptions
      };
    }
    if (type === ProviderType.ASANA) {
      const fallbackProjectOptions = savedAsanaProjectIds.map((projectId, index) => ({
        label: savedAsanaProjectNames[index] ?? projectId,
        value: projectId
      }));
      const fallbackWorkspaceOptions = savedAsanaWorkspaceGid.length > 0 ? [{
        label: savedAsanaWorkspaceName || savedAsanaWorkspaceGid,
        value: savedAsanaWorkspaceGid
      }] : [];
      return {
        projectOptions: asanaProjectOptions.length > 0 ? asanaProjectOptions : fallbackProjectOptions,
        projectOptionsLoading: isOpen && type === ProviderType.ASANA && !hasAsanaProjectOptionsRefreshSettled,
        workspaceOptions: asanaWorkspaceOptions.length > 0 ? asanaWorkspaceOptions : fallbackWorkspaceOptions
      };
    }
    if (type === ProviderType.RENTVINE) {
      return {
        vendorOptions: mergeRentvineVendorOptions({
          liveVendorOptions: rentvineVendorOptions,
          savedContactIds: savedRentvineVendorContactIds
        }),
        vendorOptionsError: rentvineVendorOptionsError,
        vendorOptionsLoading: rentvineVendorOptionsLoading
      };
    }
    return {};
  }, [type, propertyGroupOptions, savedPropertyGroupIds, savedAsanaProjectIds, savedAsanaProjectNames, savedAsanaWorkspaceGid, savedAsanaWorkspaceName, asanaWorkspaceOptions, asanaProjectOptions, isOpen, hasAsanaProjectOptionsRefreshSettled, savedRentvineVendorContactIds, rentvineVendorOptions, rentvineVendorOptionsError, rentvineVendorOptionsLoading]);
  const handleEdit = async (data_0: EditDataProviderSchema) => {
    if (!id) return;
    // Read directly from form to ensure we capture UI state
    const propertyGroupIds = (form.getValues("data.propertyGroupIds" as any) || []) as string[];
    const projectIds = (form.getValues("data.projectIds" as any) || []) as string[];
    const generalWorkOrderVendorContactIds = (form.getValues("data.credentials.generalWorkOrderVendorContactIds" as any) || []) as string[];
    const workspaceGid = form.getValues("data.credentials.workspaceGid" as any) as string ?? "";
    const workspaceOptions = (providerData.workspaceOptions || []) as {
      label: string;
      value: string;
    }[];
    const selectedWorkspace = workspaceOptions.find(option_0 => option_0.value === workspaceGid);
    const workspaceName = selectedWorkspace?.label ?? workspaceGid;
    const projectOptions = (providerData.projectOptions || []) as {
      label: string;
      value: string;
    }[];
    const projectNameLookup = new Map(projectOptions.map(projectOption => [projectOption.value, projectOption.label]));
    const projectNames = projectIds.map(projectId_0 => projectNameLookup.get(projectId_0) ?? projectId_0);
    const payload: any = {
      ...data_0,
      // top-level for API merge (PUT route)
      ...(Array.isArray(propertyGroupIds) && {
        propertyGroupIds
      }),
      ...(Array.isArray(projectIds) && {
        projectIds
      }),
      ...(Array.isArray(projectNames) && {
        projectNames
      }),
      ...(Array.isArray(generalWorkOrderVendorContactIds) && {
        generalWorkOrderVendorContactIds
      }),
      ...(workspaceGid.length > 0 && workspaceGid !== "__placeholder__" && {
        workspaceGid
      }),
      ...(workspaceGid.length > 0 && workspaceGid !== "__placeholder__" && {
        workspaceName
      }),
      data: {
        ...data_0.data,
        credentials: {
          ...data_0.data.credentials,
          ...(Array.isArray(propertyGroupIds) && {
            propertyGroupIds
          }),
          ...(Array.isArray(projectIds) && {
            projectIds
          }),
          ...(Array.isArray(projectNames) && {
            projectNames
          }),
          ...(Array.isArray(generalWorkOrderVendorContactIds) && {
            generalWorkOrderVendorContactIds
          }),
          ...(workspaceGid.length > 0 && workspaceGid !== "__placeholder__" && {
            workspaceGid
          }),
          ...(workspaceGid.length > 0 && workspaceGid !== "__placeholder__" && {
            workspaceName
          })
        }
      }
    };
    await editWorkspaceDataProvider(payload);
  };
  const initialValues = {
    data: {
      credentials: {
        apiKey: "",
        clientId: "",
        clientSecret: "",
        email: "",
        generalWorkOrderVendorContactIds: [] as string[],
        password: "",
        personalAccessToken: "",
        superAdminEmail: "",
        workspaceGid: "",
        workspaceName: ""
      },
      projectIds: [] as string[],
      // Not part of edit schema; used locally for UI state
      propertyGroupIds: [] as string[]
    },
    provider: {
      id,
      type
    },
    workspaceId
  };
  const form = useForm<EditDataProviderFormValues, unknown, EditDataProviderSchema>({
    defaultValues: initialValues,
    resolver: zodResolver(editDataProviderSchema)
  });
  const rentvineDatabaseName = form.watch("data.credentials.databaseName") as string | undefined ?? "";
  const rentvineClientId = form.watch("data.credentials.clientId") as string | undefined ?? "";
  const rentvineClientSecret = form.watch("data.credentials.clientSecret") as string | undefined ?? "";
  const providerName = PROVIDER_NAMES[type];
  useEffect(() => {
    if (!isOpen || type !== ProviderType.ASANA || !workspaceId) {
      setHasAsanaProjectOptionsRefreshSettled(false);
      return;
    }
    let isCancelled = false;
    const loadAsanaOptions = async () => {
      setHasAsanaProjectOptionsRefreshSettled(false);
      try {
        const token = form.getValues("data.credentials.personalAccessToken" as any) as string | undefined ?? "";
        const workspacePingPayload = {
          data: {
            credentials: {
              personalAccessToken: token
            },
            type: ProviderType.ASANA
          },
          workspaceId
        };
        const workspacePingResult = await pingWorkspaceDataProvider(workspacePingPayload as any);
        if (!workspacePingResult?.isCredentialsValid || isCancelled) return;
        const workspaceOptions_0 = (workspacePingResult.workspaces as any[] ?? []).map(workspace => ({
          label: String(workspace.name ?? ""),
          value: String(workspace.id ?? "")
        })).filter(workspace_0 => workspace_0.label.length > 0 && workspace_0.value.length > 0).sort((workspaceA, workspaceB) => workspaceA.label.localeCompare(workspaceB.label));
        setAsanaWorkspaceOptions(workspaceOptions_0);
        const selectedWorkspaceGid = form.getValues("data.credentials.workspaceGid" as any) as string | undefined || savedAsanaWorkspaceGid || workspaceOptions_0[0]?.value || "";
        if (!selectedWorkspaceGid) return;
        const selectedWorkspace_0 = workspaceOptions_0.find(option_1 => option_1.value === selectedWorkspaceGid);
        const selectedWorkspaceName = selectedWorkspace_0?.label ?? (savedAsanaWorkspaceName || selectedWorkspaceGid);
        form.setValue("data.credentials.workspaceGid" as any, selectedWorkspaceGid);
        form.setValue("data.credentials.workspaceName" as any, selectedWorkspaceName);
        const projectsPingPayload = {
          data: {
            credentials: {
              personalAccessToken: token,
              workspaceGid: selectedWorkspaceGid,
              workspaceName: selectedWorkspaceName
            },
            type: ProviderType.ASANA
          },
          workspaceId
        };
        const projectsPingResult = await pingWorkspaceDataProvider(projectsPingPayload as any);
        if (!projectsPingResult?.isCredentialsValid || isCancelled) return;
        const projectOptions_0 = (projectsPingResult.projects as any[] ?? []).map(project => ({
          label: String(project.name ?? ""),
          value: String(project.id ?? "")
        })).filter(project_0 => project_0.label.length > 0 && project_0.value.length > 0).sort((projectA, projectB) => projectA.label.localeCompare(projectB.label));
        setAsanaProjectOptions(projectOptions_0);
        form.setValue("data.projectOptions" as any, projectOptions_0);
      } catch {
        // Keep fallback options from stored credentials if refresh fails.
      } finally {
        if (!isCancelled) {
          setHasAsanaProjectOptionsRefreshSettled(true);
        }
      }
    };
    loadAsanaOptions();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, type, workspaceId, form, pingWorkspaceDataProvider, savedAsanaWorkspaceGid, savedAsanaWorkspaceName]);
  useEffect(() => {
    if (!isOpen || type !== ProviderType.RENTVINE || !workspaceId) {
      setRentvineVendorOptions([]);
      setRentvineVendorOptionsError(null);
      setRentvineVendorOptionsLoading(false);
      return;
    }
    if (rentvineDatabaseName.trim().length === 0) {
      setRentvineVendorOptions([]);
      setRentvineVendorOptionsError(null);
      setRentvineVendorOptionsLoading(false);
      return;
    }
    let isCancelled_0 = false;
    const loadRentvineVendorOptions = async () => {
      setRentvineVendorOptionsLoading(true);
      setRentvineVendorOptionsError(null);
      try {
        const pingResult = await pingWorkspaceDataProvider({
          data: {
            credentials: {
              clientId: rentvineClientId,
              clientSecret: rentvineClientSecret,
              databaseName: rentvineDatabaseName
            },
            type: ProviderType.RENTVINE
          },
          workspaceId
        } as any);
        if (isCancelled_0) {
          return;
        }
        if (!pingResult?.isCredentialsValid) {
          setRentvineVendorOptions([]);
          setRentvineVendorOptionsError("Unable to load vendors from Rentvine. Showing previously selected vendors only.");
          return;
        }
        const vendorOptions = Array.isArray(pingResult.vendors) ? toRentvineVendorOptions(pingResult.vendors) : [];
        setRentvineVendorOptions(vendorOptions);
        if (vendorOptions.length === 0) {
          setRentvineVendorOptionsError("No active vendors were returned from Rentvine. Showing previously selected vendors only.");
        }
      } catch {
        if (isCancelled_0) {
          return;
        }
        setRentvineVendorOptions([]);
        setRentvineVendorOptionsError("Unable to load vendors from Rentvine. Showing previously selected vendors only.");
      } finally {
        if (!isCancelled_0) {
          setRentvineVendorOptionsLoading(false);
        }
      }
    };
    const debounceTimeout = window.setTimeout(() => {
      void loadRentvineVendorOptions();
    }, 300);
    return () => {
      isCancelled_0 = true;
      window.clearTimeout(debounceTimeout);
    };
  }, [isOpen, pingWorkspaceDataProvider, rentvineClientId, rentvineClientSecret, rentvineDatabaseName, type, workspaceId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (!providerId) return;
    const nextDefaults = {
      data: {
        credentials: {
          apiKey: "",
          clientId: "",
          clientSecret: "",
          databaseName: databaseName ?? "",
          email: "",
          generalWorkOrderVendorContactIds: savedRentvineVendorContactIds,
          password: "",
          personalAccessToken: "",
          superAdminEmail: "",
          workspaceGid: savedAsanaWorkspaceGid,
          workspaceName: savedAsanaWorkspaceName
        },
        projectIds: savedAsanaProjectIds,
        propertyGroupIds: savedPropertyGroupIds
      },
      provider: {
        id: providerId,
        type: selectedProvider?.type as ProviderType ?? ProviderType.YARDI
      },
      workspaceId
    } as typeof initialValues;

    // Reset so defaultValues reflect the loaded provider; isDirty stays false until user changes
    form.reset(nextDefaults);
  }, [isOpen]);
  return <Credenza onOpenChange={open => {
    if (!open) {
      form.reset(initialValues);
      setAsanaProjectOptions([]);
      setAsanaWorkspaceOptions([]);
      setHasAsanaProjectOptionsRefreshSettled(false);
      setRentvineVendorOptions([]);
      setRentvineVendorOptionsError(null);
      setRentvineVendorOptionsLoading(false);
      controlEditDataProvider({
        data: {
          providerId: null
        },
        state: "off"
      });
    }
  }} open={isOpen}>
			<CredenzaContent>
				<CredenzaHeader>
					<CredenzaTitle>{`Update ${providerName} Credentials`}</CredenzaTitle>
					<CredenzaDescription className="sr-only">
						{providerName}
					</CredenzaDescription>
				</CredenzaHeader>
				<Form {...form}>
					<form className="w-full flex flex-col gap-5 px-4 sm:px-0" id="edit-data-provider-form" onSubmit={form.handleSubmit(formData => handleEdit(formData as EditDataProviderSchema))}>
						{(() => {
            const ProviderForm = PROVIDER_FORM_REGISTRY[type];
            if (!ProviderForm) return null;
            return <ProviderForm currentStep={1} form={form as any} isCreating={isEditing} isEditMode={true} providerData={providerData} />;
          })()}
					</form>
				</Form>
				<CredenzaFooter>
					<CredenzaClose asChild>
						<Button disabled={isEditing} variant="outline">
							Cancel
						</Button>
					</CredenzaClose>
					<Button disabled={isEditing || !form.formState.isDirty} form="edit-data-provider-form" type="submit">
						{isEditing ? "Saving..." : "Save"}
					</Button>
				</CredenzaFooter>
			</CredenzaContent>
		</Credenza>;
}